import { useContext } from 'react';

import { Badge, Button, Skeleton, type MapWorkbenchRuntimeSurfaceSectionRenderArgs } from '@workspace/ui';

import { readRuntimeSurfaceSectionComponentId } from '@/components/mapping/runtimeSurfaceRenderers';
import { SalesMapRoutingContext } from './SalesMapRoutingQuoteSection';
import { useSalesLocationSummary } from './useSalesLocationSummary';
import { formatRelativeOrShort } from './salesMapShared';

export const SALES_MAP_ORDERS_COMPONENT_ID = 'sales_location_orders';

export const isSalesMapOrdersSection = (section: MapWorkbenchRuntimeSurfaceSectionRenderArgs['section']) =>
  section.kind === 'custom' && readRuntimeSurfaceSectionComponentId(section) === SALES_MAP_ORDERS_COMPONENT_ID;

const formatApiLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const STATUS_VARIANT_MAP: Record<string, 'outline' | 'success' | 'destructive' | 'muted'> = {
  active: 'success',
  complete: 'muted',
  completed: 'muted',
  cancelled: 'destructive',
  canceled: 'destructive',
};

const resolveStatusVariant = (status: string): 'outline' | 'success' | 'destructive' | 'muted' =>
  STATUS_VARIANT_MAP[status?.toLowerCase()] ?? 'outline';

function SalesMapOrdersSectionBody({ feature }: MapWorkbenchRuntimeSurfaceSectionRenderArgs) {
  const routingContext = useContext(SalesMapRoutingContext);
  const { requestLocation, summary, error, isLoading, refresh } = useSalesLocationSummary(
    feature,
    routingContext?.salesContext,
  );

  if (!requestLocation) {
    return <p className="text-sm text-muted-foreground">Location identity is unavailable for order history.</p>;
  }

  const orderSummary = summary?.order_summary ?? null;
  const recentOrders = summary?.recent_orders ?? [];

  return (
    <section className="space-y-4">
      {isLoading ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
          <Skeleton className="h-10 rounded-md" />
          <Skeleton className="h-10 rounded-md" />
          <Skeleton className="h-10 rounded-md" />
        </div>
      ) : null}

      {!isLoading && orderSummary ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="muted">{orderSummary.total_order_count} total</Badge>
          <Badge variant="outline">{orderSummary.active_order_count} active</Badge>
          <Badge variant="outline">{orderSummary.priority_order_count} priority</Badge>
          {orderSummary.latest_order_at ? (
            <Badge variant="outline">Latest {formatRelativeOrShort(orderSummary.latest_order_at)}</Badge>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-md bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <div>{error instanceof Error ? error.message : 'Failed to load order history.'}</div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-auto px-0 py-0 text-xs text-destructive hover:bg-transparent hover:text-destructive/80"
            onClick={() => void refresh()}
          >
            Try again
          </Button>
        </div>
      ) : null}

      {!isLoading && !error && recentOrders.length === 0 ? (
        <p className="text-sm text-muted-foreground">No orders are recorded for this location yet.</p>
      ) : null}

      {!isLoading && !error && recentOrders.length > 0 ? (
        <div className="divide-y divide-border/40 rounded-lg border border-border/70 bg-background">
          {recentOrders.map(order => (
            <div
              key={order.order_id}
              className="flex min-w-0 items-center gap-3 px-4 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                  {order.friendly_order_id || order.order_id}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatRelativeOrShort(order.updated_at)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Badge variant={resolveStatusVariant(order.status)}>{formatApiLabel(order.status)}</Badge>
                <Badge variant="muted">{formatApiLabel(order.lifecycle_phase)}</Badge>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function renderSalesMapOrdersSection(args: MapWorkbenchRuntimeSurfaceSectionRenderArgs) {
  if (!isSalesMapOrdersSection(args.section)) {
    return null;
  }
  return <SalesMapOrdersSectionBody {...args} />;
}
